/**
 * Insect Side-Effect Calculator — Storefront JS
 * Loaded by insect-calculator.liquid
 */
(function () {
  "use strict";

  // ── Config ──────────────────────────────────────────────────────────────────
  const ROOT    = document.getElementById("isc-app");
  if (!ROOT) return;

  const API_BASE = (ROOT.dataset.apiBase || "").replace(/\/$/, "");

  // ── Element refs ────────────────────────────────────────────────────────────
  const organismInput   = document.getElementById("isc-organism-input");
  const organismList    = document.getElementById("isc-organism-list");
  const organismHint    = document.getElementById("isc-organism-hint");
  const ingredientWrap  = document.getElementById("isc-ingredient-wrap");
  const ingredientInput = document.getElementById("isc-ingredient-input");
  const ingredientList  = document.getElementById("isc-ingredient-list");
  const searchBtn       = document.getElementById("isc-search-btn");
  const clearBtn        = document.getElementById("isc-clear-btn");
  const statusEl        = document.getElementById("isc-status");
  const resultsEl       = document.getElementById("isc-results");
  const countEl         = document.getElementById("isc-count");
  const tbodyEl         = document.getElementById("isc-tbody");
  const noResultsEl     = document.getElementById("isc-no-results");
  const exportBtn       = document.getElementById("isc-export-btn");

  // ── State ────────────────────────────────────────────────────────────────────
  let allOrganisms    = [];    // [{name, commonName, group}]
  let allIngredients  = [];    // [string]
  let selectedOrganism    = "";
  let selectedIngredient  = "";
  let lastResults = [];

  // ── Boot ─────────────────────────────────────────────────────────────────────
  init();

  async function init() {
    showStatus("Loading organism list…");
    try {
      const data = await apiFetch("/api/organisms");
      allOrganisms = data.organisms || [];
      hideStatus();
    } catch (e) {
      showStatus("⚠️ Could not load data. Please refresh the page.", false);
      return;
    }
    bindEvents();
  }

  // ── Turnstile helper ─────────────────────────────────────────────────────────
  function getToken() {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), 5000);
      if (!window.turnstile) { clearTimeout(timer); resolve(null); return; }
      const container = document.getElementById("isc-turnstile");
      if (!container) { clearTimeout(timer); resolve(null); return; }
      turnstile.execute(container, {
        callback: (token) => { clearTimeout(timer); resolve(token); },
        "error-callback": () => { clearTimeout(timer); resolve(null); },
      });
    });
  }

  // ── API helper ───────────────────────────────────────────────────────────────
  async function apiFetch(path, params = {}, token = null) {
    const url = new URL(API_BASE + path);
    Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
    url.searchParams.set("_ts", Date.now()); // cache-buster
    const headers = {};
    if (token) headers["X-Turnstile-Token"] = token;
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) {
      let body = "";
      try { body = await res.text(); } catch {}
      throw new Error(`API ${res.status}: ${path} — ${body.slice(0, 200)}`);
    }
    return res.json();
  }

  // ── Combobox logic ────────────────────────────────────────────────────────────
  function buildCombo(input, listEl, getOptions, onSelect, renderItem) {
    let activeIdx = -1;
    let open = false;

    function filter(query) {
      const q = query.toLowerCase().trim();
      return getOptions().filter(opt => {
        const name   = typeof opt === "string" ? opt : opt.name;
        const common = (opt && opt.commonName) ? opt.commonName : "";
        return name.toLowerCase().includes(q) || common.toLowerCase().includes(q);
      }).slice(0, 80);
    }

    function highlight(text, q) {
      if (!q) return escHtml(text);
      const idx = text.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return escHtml(text);
      return escHtml(text.slice(0, idx)) +
        "<mark>" + escHtml(text.slice(idx, idx + q.length)) + "</mark>" +
        escHtml(text.slice(idx + q.length));
    }

    function render(items) {
      if (!items.length) { close(); return; }
      listEl.innerHTML = items.map((item, i) => renderItem(item, i, input, highlight)).join("");
      listEl.removeAttribute("hidden");
      input.setAttribute("aria-expanded", "true");
      open = true;
      activeIdx = -1;
    }

    function close() {
      listEl.innerHTML = "";
      listEl.setAttribute("hidden", "");
      input.setAttribute("aria-expanded", "false");
      open = false;
      activeIdx = -1;
    }

    function selectItem(value) {
      input.value = value;
      close();
      onSelect(value);
    }

    input.addEventListener("input", () => {
      const q = input.value.trim();
      if (!q) {
        // Show full list when field is cleared rather than closing
        const all = getOptions();
        if (all.length && all.length <= 200) {
          render(all);
        } else {
          close();
        }
        onSelect("");
        return;
      }
      render(filter(q));
    });

    input.addEventListener("focus", () => {
      const q = input.value.trim();
      if (q) {
        // Reopen with filtered list when refocusing on a filled field
        render(filter(q));
      } else if (getOptions().length <= 200) {
        render(getOptions());
      }
    });

    input.addEventListener("keydown", (e) => {
      const items = listEl.querySelectorAll("li");
      if (!open || !items.length) {
        if (e.key === "Enter") { onSelect(input.value.trim()); }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        activeIdx = (activeIdx + 1) % items.length;
        updateActive(items);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        activeIdx = (activeIdx - 1 + items.length) % items.length;
        updateActive(items);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIdx >= 0) {
          selectItem(items[activeIdx].dataset.value);
        } else {
          close();
          onSelect(input.value.trim());
        }
      } else if (e.key === "Escape") {
        close();
      }
    });

    function updateActive(items) {
      items.forEach((li, i) => li.setAttribute("aria-selected", i === activeIdx ? "true" : "false"));
      if (activeIdx >= 0) items[activeIdx].scrollIntoView({ block: "nearest" });
    }

    listEl.addEventListener("click", (e) => {
      const li = e.target.closest("li");
      if (li) selectItem(li.dataset.value);
    });

    document.addEventListener("click", (e) => {
      if (!input.contains(e.target) && !listEl.contains(e.target)) close();
    });
  }

  // ── Bind events ───────────────────────────────────────────────────────────────
  function bindEvents() {
    // Organism combo — shows "Scientific Name | Common Name" + group tag
    buildCombo(
      organismInput,
      organismList,
      () => allOrganisms,
      async (value) => {
        selectedOrganism = value;
        selectedIngredient = "";
        ingredientInput.value = "";

        if (!value) {
          disableIngredient();
          searchBtn.disabled = true;
          organismHint.textContent = "";
          return;
        }

        const match = allOrganisms.find(o => o.name.toLowerCase() === value.toLowerCase());
        if (match) {
          selectedOrganism = match.name;
          const hintParts = [];
          if (match.commonName) hintParts.push(match.commonName);
          if (match.group)      hintParts.push(`Group: ${match.group}`);
          organismHint.textContent = hintParts.join(" · ");
        }

        enableIngredient();
        searchBtn.disabled = false;

        showStatus("Loading active ingredients for this organism…");
        try {
          const data = await apiFetch("/api/ingredients", { organism: selectedOrganism });
          allIngredients = data.ingredients || [];
          hideStatus();
        } catch {
          allIngredients = [];
          hideStatus();
        }
      },
      // custom renderer: shows common name + group tag
      (item, i, input, highlight) => {
        const name   = typeof item === "string" ? item : item.name;
        const common = (item && item.commonName) ? item.commonName : "";
        const group  = (item && item.group) ? item.group : "";
        const q = input.value.trim();
        const display = common
          ? `${highlight(name, q)} <span class="isc-common-name">| ${highlight(common, q)}</span>`
          : highlight(name, q);
        return `<li role="option" data-value="${escAttr(name)}" data-idx="${i}">
          ${display}
          ${group ? `<span class="isc-group-tag">${escHtml(group)}</span>` : ""}
        </li>`;
      }
    );

    // Ingredient combo — plain string list
    buildCombo(
      ingredientInput,
      ingredientList,
      () => allIngredients,
      (value) => { selectedIngredient = value; },
      (item, i, input, highlight) => {
        const name = typeof item === "string" ? item : item.name;
        const q = input.value.trim();
        return `<li role="option" data-value="${escAttr(name)}" data-idx="${i}">
          ${highlight(name, q)}
        </li>`;
      }
    );

    searchBtn.addEventListener("click", doSearch);
    ingredientInput.addEventListener("keydown", (e) => { if (e.key === "Enter") doSearch(); });
    clearBtn.addEventListener("click", reset);
    exportBtn.addEventListener("click", exportCsv);
  }

  // ── Search ───────────────────────────────────────────────────────────────────
  async function doSearch() {
    if (!selectedOrganism) return;

    showStatus(`<div class="isc-spinner"></div> Searching…`, false);
    hideResults();
    clearBtn.removeAttribute("hidden");

    try {
      const params = { organism: selectedOrganism };
      if (selectedIngredient) params.ingredient = selectedIngredient;

      const token = await getToken();
      const data = await apiFetch("/api/results", params, token);

      // Reset Turnstile so it can be used again
      if (window.turnstile) {
        const container = document.getElementById("isc-turnstile");
        if (container) turnstile.reset(container);
      }

      // Deduplicate: keep only the highest-toxicity entry per active ingredient
      const _raw = data.results || [];
      const _bestMap = new Map();
      _raw.forEach(r => {
        const _k = (r.activeIngredient || "").toLowerCase().replace(/\s+/g, " ").trim();
        const _ex = _bestMap.get(_k);
        if (!_ex || (r.toxicity || 0) > (_ex.toxicity || 0)) _bestMap.set(_k, r);
      });
      lastResults = [..._bestMap.values()].sort((a, b) => {
        const _ai = (a.activeIngredient || "").toLowerCase();
        const _bi = (b.activeIngredient || "").toLowerCase();
        if (_ai !== _bi) return _ai.localeCompare(_bi);
        return (b.toxicity || 0) - (a.toxicity || 0);
      });
      hideStatus();
      renderResults(lastResults);
    } catch (e) {
      showStatus(`⚠️ Search failed: ${e && e.message ? e.message : String(e)}`, false);
    }
  }

  // ── Render results ────────────────────────────────────────────────────────────
  function renderResults(results) {
    if (!results.length) {
      noResultsEl.removeAttribute("hidden");
      return;
    }

    const label = selectedIngredient
      ? `${results.length} result${results.length !== 1 ? "s" : ""} for <strong>${escHtml(selectedOrganism)}</strong> + <strong>${escHtml(selectedIngredient)}</strong>`
      : `${results.length} result${results.length !== 1 ? "s" : ""} for <strong>${escHtml(selectedOrganism)}</strong>`;
    countEl.innerHTML = label;

    tbodyEl.innerHTML = results.map(r => {
      const toxClass = r.toxicity ? `isc-tox--${r.toxicity}` : "";
      const toxTitle = r.toxicityLabel || "";
      return `<tr>
        <td>
          ${escHtml(r.organism)}
          ${r.commonName ? `<br><span class="isc-common-name">${escHtml(r.commonName)}</span>` : ""}
        </td>
        <td><strong>${escHtml(r.activeIngredient)}</strong></td>
        <td>
          ${r.toxicity
            ? `<span class="isc-tox ${toxClass}" title="${escAttr(toxTitle)}">${r.toxicity}</span>`
            : `<span class="isc-muted">—</span>`}
        </td>
        <td>${escHtml(r.dose) || '<span class="isc-muted">—</span>'}</td>
        <td>${escHtml(r.organismGroup) || '<span class="isc-muted">—</span>'}</td>
        <td class="isc-muted">${escHtml(r.iracMainGroup) || '—'}</td>
        <td class="isc-muted">${escHtml(r.iracSubGroup) || '—'}</td>
        <td>${escHtml(r.fracMoa) || '<span class="isc-muted">—</span>'}</td>
        <td class="isc-muted">${escHtml(r.fracTargetSite) || '—'}</td>
        <td>${escHtml(r.fracGroupCode) || '<span class="isc-muted">—</span>'}</td>
      </tr>`;
    }).join("");

    resultsEl.removeAttribute("hidden");
    exportBtn.style.display = "";
  }

  // ── Export CSV ────────────────────────────────────────────────────────────────
  function exportCsv() {
    if (!lastResults.length) return;

    const cols = [
      "Organism", "Common Name", "Active Ingredient", "Toxicity", "Toxicity Label",
      "Dose", "Organism Group",
      "IRAC Main Group & Site", "IRAC Sub-Group",
      "FRAC MOA", "FRAC Target Site", "FRAC Group Code",
    ];
    const rows = lastResults.map(r => [
      r.organism, r.commonName, r.activeIngredient, r.toxicity, r.toxicityLabel,
      r.dose, r.organismGroup,
      r.iracMainGroup, r.iracSubGroup,
      r.fracMoa, r.fracTargetSite, r.fracGroupCode,
    ]);

    const csv = [cols, ...rows]
      .map(row => row.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `side-effects-${selectedOrganism.replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  function reset() {
    selectedOrganism   = "";
    selectedIngredient = "";
    organismInput.value   = "";
    ingredientInput.value = "";
    organismHint.textContent = "";
    allIngredients = [];
    searchBtn.disabled = true;
    clearBtn.setAttribute("hidden", "");
    disableIngredient();
    hideStatus();
    hideResults();
    noResultsEl.setAttribute("hidden", "");
    lastResults = [];
  }

  function enableIngredient() {
    ingredientInput.disabled = false;
    ingredientWrap.removeAttribute("aria-disabled");
  }
  function disableIngredient() {
    ingredientInput.disabled = true;
    ingredientWrap.setAttribute("aria-disabled", "true");
  }

  function showStatus(html, spinner = true) {
    statusEl.innerHTML = spinner
      ? `<div class="isc-spinner" aria-hidden="true"></div>${html}`
      : html;
    statusEl.removeAttribute("hidden");
  }
  function hideStatus() {
    statusEl.setAttribute("hidden", "");
    statusEl.innerHTML = "";
  }
  function hideResults() {
    resultsEl.setAttribute("hidden", "");
    noResultsEl.setAttribute("hidden", "");
    exportBtn.style.display = "none";
    tbodyEl.innerHTML = "";
  }

  function escHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
  function escAttr(str) { return escHtml(str); }

})();
